import features from '@nobia/zeus-components/lib/features'
import generateClassName from '@nobia/zeus-components/lib/helpers/generateClassName'
import { NavLink as NavLinkUI, useLocation } from '@nobia/zeus-components/lib/router'
import settings from '@nobia/zeus-components/lib/settings'
import styled, { theme } from '@nobia/zeus-components/lib/styled'
import rem from '@nobia/zeus-components/lib/styled/rem'
import React, { useMemo, useEffect, useState } from 'react'

import {
  canNavigateToDesignsPage,
  canNavigateToMessagesPage,
  canNavigateToOrderPage,
  useRoutes,
} from '../../routes'
import { canNavigateToAppointmentsPage } from '../../routes/useRoutes'
import { useAccountContext } from '../AccountContext'
import { useUser } from '../user-provider/UserProvider'
import { useSignalR } from '../signalr-provider/SignalRProvider'
import NavigationItem from './NavigationItem'
import { HomePageQuery } from '../../pages/home/HomePageQuery'
import { useSwrQuery } from '../../utils/swrHooks'

const Fixed = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  padding: ${theme('navigation.wrapper.padding')};
  box-shadow: ${theme('navigation.wrapper.boxShadow')};
  border-top: ${theme('navigation.wrapper.border')};
  background: ${theme('navigation.wrapper.background')};
  height: 93px;

  ${(p) => p.theme.media.min.lg`
    width: ${theme('navigation.wrapper.lg.width', 'auto')};
    height: 100%;
    box-shadow: none;
    border-top: none;
    border-right: ${theme('navigation.wrapper.border')};
    padding: ${theme('navigation.wrapper.lg.padding')};
    top: var(--header-total-height, ${p.theme.header.wrapper?.md?.height || rem(110)});
  `}
`

const Navigation = styled.nav``

const NavList = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: ${rem(460)};
  margin: 0 auto;
  ${(p) => p.theme.media.min.lg`
    flex-direction: column;
  `}
`

const NavItem = styled.div`
  flex-grow: 1;
  width: 100%;
  ${(p) => p.theme.media.min.lg`
    margin-bottom: ${rem(10)};
  `}
`

const activeClassName = generateClassName('NavLink.activeClassName')
const NavLink = styled(NavLinkUI).attrs({
  activeClassName,
})`
  padding: 10px 0;
  color: ${theme('navigation.item.color')};
  text-decoration: none;
  display: block;

  &:hover,
  &.${activeClassName} {
    color: ${theme('navigation.item.activeColor')};
  }
  &.${activeClassName} {
    background-color: ${theme('navigation.item.activeBackgroundColor')};
    border-radius: ${(p) => rem(p.theme.navigation.item.borderRadius)};
  }
`

function NavigationComponent() {
  const { store, staffMember } = useAccountContext()
  const { home, appointment, designs, messages, order, saved } = useRoutes()
  const { customer, reload } = useUser()
  const { onNewMessage, offNewMessage } = useSignalR()
  const location = useLocation()

  const { data: homePageData } = useSwrQuery(HomePageQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      storeId: store?.id,
      multiStore: features.multiStore === true,
    },
  })

  const isOrderCreated = homePageData?.me?.customer?.projectState?.orderCreated && homePageData?.me?.customer?.projectState?.order?.id
  const orderPaid = homePageData?.me?.customer?.projectState?.orderPaid

  const [cachedCustomerMessagingEnabled, setCachedCustomerMessagingEnabled] = React.useState(() =>
    JSON.parse(sessionStorage.getItem('cachedCustomerMessagingEnabled'))
  );
  const [cachedStore, setCachedStore] = React.useState(() =>
    JSON.parse(sessionStorage.getItem('cachedStore')) || null
  );
  const [cachedStaffMember, setCachedStaffMember] = React.useState(() =>
    JSON.parse(sessionStorage.getItem('cachedStaffMember')) || null
  );

  const [showOrderNotification, setShowOrderNotification] = useState(() => {
    const stored = localStorage.getItem('orderNotificationState')
    if (!stored) return isOrderCreated
    const state = JSON.parse(stored)
    return state.shouldShow !== false
  });

  useEffect(() => {
    if (customer !== undefined && customer?.messagingEnabled !== undefined) {
      setCachedCustomerMessagingEnabled(customer.messagingEnabled);
      sessionStorage.setItem(
        'cachedCustomerMessagingEnabled',
        JSON.stringify(customer.messagingEnabled)
      );
    }
  }, [customer]);

  useEffect(() => {
    if (store !== undefined) {
      setCachedStore(store);
      sessionStorage.setItem('cachedStore', JSON.stringify(store));
    }
  }, [store]);

  useEffect(() => {
    if (staffMember !== undefined) {
      setCachedStaffMember(staffMember);
      sessionStorage.setItem('cachedStaffMember', JSON.stringify(staffMember));
    }
  }, [staffMember]);

  const messagingEnabled = useMemo(
    () => cachedCustomerMessagingEnabled === "true" || cachedCustomerMessagingEnabled === true || false,
    [cachedCustomerMessagingEnabled]
  )

  useEffect(() => {
    if (!isOrderCreated) return
    
    const stored = localStorage.getItem('orderNotificationState')
    const storedState = stored ? JSON.parse(stored) : null
    
    if (orderPaid) {
      localStorage.setItem('orderNotificationState', JSON.stringify({
        orderPaid,
        shouldShow: false,
      }))
      setShowOrderNotification(false)
      return
    }

    if (!storedState) {
      localStorage.setItem('orderNotificationState', JSON.stringify({
        orderPaid,
        shouldShow: true,
      }))
      setShowOrderNotification(true)
      return
    }

    const storedOrderPaid = storedState.orderPaid

    const hasChanged = 
      orderPaid !== storedOrderPaid

    if (hasChanged) {
      localStorage.setItem('orderNotificationState', JSON.stringify({
        orderPaid,
        shouldShow: true,
      }))
      setShowOrderNotification(true)
    } else {
      const shouldShow = storedState.shouldShow !== false
      setShowOrderNotification(shouldShow)
    }
  }, [isOrderCreated, orderPaid])

  useEffect(() => {
    if (location.pathname.startsWith('/order')) {
      const stored = localStorage.getItem('orderNotificationState')
      const storedState = stored ? JSON.parse(stored) : {}
      
      localStorage.setItem('orderNotificationState', JSON.stringify({
        ...storedState,
        orderPaid,
        shouldShow: false,
      }))
      
      setShowOrderNotification(false)
    }
  }, [location.pathname, orderPaid])

  useEffect(() => {
    if (!messagingEnabled) return

    const handleNewMessage = () => {
      reload()
    }

    onNewMessage(handleNewMessage)

    return () => {
      offNewMessage(handleNewMessage)
    }
  }, [messagingEnabled, reload, onNewMessage, offNewMessage])

  return (
    <Fixed>
      <Navigation>
        <NavList>
          <NavItem>
            <NavLink to={home.path} exact>
              <NavigationItem label={home.title} icon="checkmark-circle" />
            </NavLink>
          </NavItem>

          {features.appointment && canNavigateToAppointmentsPage(store?.id) && (
            <NavItem>
              <NavLink to={appointment.path}>
                <NavigationItem label={appointment.title} icon="appointment" />
              </NavLink>
            </NavItem>
          )}

          {features.designQuotes && canNavigateToDesignsPage(store?.id) ? (
            <NavItem>
              <NavLink to={designs.path}>
                <NavigationItem label={designs.title} icon="designs" />
              </NavLink>
            </NavItem>
          ) : null}

          {features.orders && canNavigateToOrderPage(store?.id) ? (
            <NavItem>
              <NavLink to={order.path}>
                <NavigationItem 
                  label={order.title} 
                  icon="order" 
                  notification={showOrderNotification}
                />
              </NavLink>
            </NavItem>
          ) : null}

          {features.messaging &&
          canNavigateToMessagesPage(cachedStore?.id) &&
          cachedStaffMember &&
          messagingEnabled ? (
            <NavItem>
              <NavLink to={messages.path}>
                <NavigationItem
                  label={messages.title}
                  icon="messages"
                  notification={
                    features.messaging === true &&
                    customer?.unreadStoreMessages > 0 &&
                    !location.pathname.startsWith(messages.path)
                  }
                />
              </NavLink>
            </NavItem>
          ) : null}

          <NavItem>
            <NavLink to={saved.path}>
              <NavigationItem
                label={saved.title}
                icon={
                  ['hthdk', 'hthse', 'hthno'].includes(settings.brand)
                    ? 'heart'
                    : 'bookmark'
                }
              />
            </NavLink>
          </NavItem>
        </NavList>
      </Navigation>
    </Fixed>
  )
}

NavigationComponent.displayName = 'NavigationComponent'

export default NavigationComponent